/* import __COLOCATED_TEMPLATE__ from './common-primary-nav-bar.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { modifier } from 'ember-modifier';
import RouteRegexes from 'embercom/lib/route-regexes';
import type CommonPrimaryNavBarService from 'embercom/services/common-primary-nav-bar-service';
import {
  NAV_BAR_COLLAPSED_WIDTH_PX,
  NAV_BAR_EXPANDED_WIDTH_PX,
} from 'embercom/services/common-primary-nav-bar-service';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type InboxState from 'embercom/services/inbox-state';
import { SidebarViewType } from 'embercom/services/inbox-state';

export type NavBarEntry = {
  id: NavBarEntryId;
  productName: string;
  route: string;
  icon?: InterfaceIconName;
  activeRoutes: string[];
  className?: string;
  analyticsEventObjectName?: string;
  dataIntercomTarget?: string;
  canUseProduct: boolean;
  queryParams?: Record<string, any>;
};

type NavBarEntryId =
  | 'getting-started'
  | 'settings'
  | 'inbox'
  | 'ai-automation'
  | 'knowledge-hub'
  | 'reports'
  | 'outbound'
  | 'contacts'
  | 'standalone'
  | 'manage-migration'
  | 'workforce'
  | 'referrals';

interface Args {
  app: $TSFixMe;
  isChannelSwitcherActive: boolean;
  inboxCount: number;
  canUseInbox: boolean;
  canUseOutbound: boolean;
  canUseWorkforce?: boolean;
  inInbox?: boolean;
  disableUserMenu?: boolean;
}

interface Signature {
  Args: Args;
}

const COMMON_PRIMARY_NAV = 'common-primary-nav';

export default class CommonPrimaryNavBar extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare commonPrimaryNavBarService: CommonPrimaryNavBarService;
  @service declare greatGuidanceService: GreatGuidanceService;
  @service declare inboxState: InboxState;
  @service declare navbarCollapsingService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked navItemInFocus: string | null = null;
  @tracked expandNavbar = false;
  @tracked fullyExpanded = false;
  @tracked fullyCollapsed = false;
  @tracked userMenuOpen = false;
  @tracked timeout: ReturnType<typeof setTimeout> | null = null;

  constructor(owner: $TSFixMe, args: Args) {
    super(owner, args);
    this.commonPrimaryNavBarService.initialise(this.workspaceID);
    this.fullyExpanded =
      this.commonPrimaryNavBarService.pinned ||
      this.router.currentRouteName.startsWith('apps.app.getting-started');
    this.fullyCollapsed = !this.commonPrimaryNavBarService.pinned;
    this.router.on('routeWillChange', () => {
      if (this.onInboxRoute) {
        this.setWidgetVisibility(false);
      }
    });
  }

  sidebar = modifier(
    (navBar: Element) => {
      let onTrasitionStart = (event: TransitionEvent) => {
        if (event.propertyName === 'width') {
          this.fullyExpanded = false;
          this.fullyCollapsed = false;
        }
      };

      let onTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName === 'width') {
          let navContentElementWidth = (event.target as HTMLDivElement).offsetWidth;
          this.fullyCollapsed = navContentElementWidth === NAV_BAR_COLLAPSED_WIDTH_PX;
          this.fullyExpanded = navContentElementWidth === NAV_BAR_EXPANDED_WIDTH_PX;
        }
      };
      navBar.addEventListener('transitionstart', onTrasitionStart);
      navBar.addEventListener('transitionend', onTransitionEnd);

      return function () {
        navBar.removeEventListener('transitionstart', onTrasitionStart);
        navBar.removeEventListener('transitionend', onTransitionEnd);
      };
    },
    { eager: false },
  );

  get workspaceID() {
    return this.args.app.id;
  }

  get canSeeSecondaryNav() {
    return this.onInboxRoute
      ? this.inboxState.activeSidebarView !== SidebarViewType.Hidden
      : !this.navbarCollapsingService.collapsed;
  }

  get onInboxRoute() {
    return (
      this.router.currentRouteName.match(RouteRegexes.inbox) ||
      this.router.currentRouteName.match(RouteRegexes.inbox2) ||
      this.router.currentRouteName.startsWith('inbox_loading')
    );
  }

  get shouldShowRadialIndicator() {
    return (
      (this.totalSteps !== this.completedSteps &&
        this.router.currentRouteName.match(RouteRegexes.gettingStarted)) ||
      (!this.greatGuidanceService.hideFloatingWidget &&
        (!this.greatGuidanceService.widgetVisibility || !this.canSeeSecondaryNav))
    );
  }

  get totalSteps() {
    return this.greatGuidanceService.steps.length;
  }

  get completedSteps() {
    return this.greatGuidanceService.steps.filter((step) => step.state === 'completed').length;
  }

  private get navEntries(): Record<NavBarEntryId, NavBarEntry> {
    let entries: Partial<Record<NavBarEntryId, NavBarEntry>> = {
      'getting-started': {
        id: 'getting-started',
        productName: this.intl.t('components.primary-nav.get-started'),
        route: 'apps.app.getting-started',
        activeRoutes: ['apps.app.getting-started'],
        analyticsEventObjectName: 'getting_started_home_primary_nav_icon',
        dataIntercomTarget: 'data-test-guide-library-link',
        canUseProduct: true,
      },
      settings: {
        id: 'settings',
        icon: 'settings',
        productName: this.intl.t('components.primary-nav.settings'),
        route: 'apps.app.settings',
        activeRoutes: ['apps.app.settings'],
        analyticsEventObjectName: 'product_settings_primary_nav_icon',
        dataIntercomTarget: 'ia-settings-nav-item',
        canUseProduct: true,
      },
      inbox: {
        id: 'inbox',
        icon: 'inbox',
        productName: this.intl.t('components.primary-nav.inbox'),
        route: 'apps.app.inbox',
        activeRoutes: ['inbox.workspace.inbox'],
        canUseProduct: this.args.canUseInbox,
        dataIntercomTarget: 'pia-nav-inbox',
      },
      'ai-automation': {
        id: 'ai-automation',
        icon: 'fin',
        productName: this.intl.t('components.primary-nav.fin-ai-agent'),
        route: 'apps.app.automation.fin-ai-agent.setup',
        activeRoutes: ['apps.app.automation'],
        canUseProduct: true,
        dataIntercomTarget: 'pia-nav-ai-automation',
      },
      'knowledge-hub': {
        id: 'knowledge-hub',
        icon: 'knowledge',
        productName: this.intl.t('components.primary-nav.knowledge-base'),
        route: 'apps.app.knowledge-hub',
        activeRoutes: ['apps.app.knowledge-hub'],
        canUseProduct: true,
        dataIntercomTarget: 'pia-nav-knowledge-hub',
      },
      reports: {
        id: 'reports',
        icon: 'column-chart',
        productName: this.intl.t('components.primary-nav.reports'),
        route: 'apps.app.reports',
        activeRoutes: ['apps.app.reports'],
        analyticsEventObjectName: 'reporting_side_bar_link',
        canUseProduct: true,
        dataIntercomTarget: 'pia-nav-reports',
      },
      outbound: {
        id: 'outbound',
        icon: 'outbound-filled',
        productName: this.intl.t('components.primary-nav.outbound'),
        route: 'apps.app.outbound',
        activeRoutes: ['apps.app.outbound'],
        canUseProduct: this.args.canUseOutbound,
        dataIntercomTarget: 'pia-nav-outbound',
      },
      contacts: {
        id: 'contacts',
        icon: 'multiple-people',
        productName: this.intl.t('components.primary-nav.contacts'),
        route: 'apps.app.users',
        activeRoutes: ['apps.app.users', 'apps.app.companies'],
        canUseProduct: true,
        dataIntercomTarget: 'pia-nav-contacts',
      },
      workforce: {
        id: 'workforce',
        icon: 'schedule',
        productName: this.intl.t('components.primary-nav.workforce'),
        route: 'apps.app.workforce',
        activeRoutes: ['apps.app.workforce'],
        canUseProduct: this.args.canUseWorkforce ?? false,
        dataIntercomTarget: 'pia-nav-workforce',
      },
      referrals: {
        id: 'referrals',
        icon: 'gift',
        productName: this.intl.t('components.primary-nav.earn-100'),
        route: 'apps.app.settings.workspace.referrals',
        activeRoutes: ['apps.app.settings.workspace.referrals'],
        analyticsEventObjectName: 'referrals_primary_nav_icon',
        canUseProduct: true,
        dataIntercomTarget: 'pia-nav-referrals',
      },
    };

    return entries as Record<NavBarEntryId, NavBarEntry>;
  }

  get greatGuidanceWidgetEntry(): NavBarEntry {
    return this.navEntries['getting-started'];
  }

  get settingsEntry(): NavBarEntry {
    return this.navEntries.settings;
  }

  get referralsEntry(): NavBarEntry {
    return this.navEntries.referrals;
  }

  get shouldShowReferralsNavItem(): boolean {
    let app = this.args.app;
    if (!app) {
      return false;
    }
    // app.get() is an Ember Data method — tests may pass plain object stubs without it
    let getValue = (key: string) => (typeof app.get === 'function' ? app.get(key) : app[key]);
    return Boolean(
      getValue('hasActiveSubscription') &&
      !getValue('inTrial') &&
      !getValue('isSalesforceContracted'),
    );
  }

  get isSettingsActiveExcludingReferrals(): boolean {
    let currentRoute = this.router.currentRouteName;
    if (currentRoute.startsWith('apps.app.settings.workspace.referrals')) {
      return false;
    }
    return this.currentActiveRoute(this.settingsEntry.activeRoutes);
  }

  get suiteToStandaloneNavItem(): NavBarEntry | undefined {
    // We are deliberately not using the `componentData` approach to making this generic across all
    // platforms as this component is rendered really early (before workspace data has loaded sometimes)
    // and we don't want to rely on the Platform object being available.
    if (
      this.args.app?.isMigratingFromStandaloneToSuite &&
      !!this.args.app.standalonePlatforms?.length
    ) {
      let platform = this.args.app.standalonePlatforms[0].platform_type;

      let iconMapping: Record<string, InterfaceIconName> = {
        zendesk: 'zendesk',
        salesforce: 'salesforce',
        hubspot: 'hubspot',
        freshdesk: 'freshdesk',
      } as const;

      let icon: InterfaceIconName = iconMapping[platform as string] ?? 'circle-filled';

      let productNameMapping: Record<string, string> = {
        zendesk: 'components.primary-nav.fin-for-zendesk',
        salesforce: 'components.primary-nav.fin-for-salesforce',
        hubspot: 'components.primary-nav.fin-for-hubspot',
        freshdesk: 'components.primary-nav.fin-for-freshdesk',
      };

      let productNameKey =
        productNameMapping[platform as string] ?? 'components.primary-nav.fin-for-generic';

      let productName = this.intl.t(productNameKey);

      return {
        id: 'standalone',
        icon,
        productName,
        route: 'apps.app.standalone',
        activeRoutes: ['apps.app.standalone'],
        canUseProduct: true,
        dataIntercomTarget: 'pia-nav-standalone',
        queryParams: { suite: null },
      };
    }

    return undefined;
  }

  get manageMigrationEntry(): NavBarEntry | undefined {
    if (!this.args.app?.canShowManageMigration) {
      return undefined;
    }

    return {
      id: 'manage-migration',
      icon: 'transfer',
      productName: this.intl.t('standalone.navbar.manage-migration'),
      route: 'apps.app.manage-migration.index',
      activeRoutes: ['apps.app.manage-migration'],
      canUseProduct: true,
      dataIntercomTarget: 'pia-nav-manage-migration',
      queryParams: { suite: '1' },
    };
  }

  get topNavEntries(): NavBarEntry[] {
    if (this.showExpiredSubscriptionNavBar) {
      return [this.navEntries.contacts];
    }
    return [
      this.navEntries.inbox,
      this.navEntries['ai-automation'],
      this.navEntries['knowledge-hub'],
      this.navEntries.reports,
      this.navEntries.outbound,
      this.navEntries.contacts,
      ...(this.args.canUseWorkforce ? [this.navEntries.workforce] : []),
    ];
  }

  get homeButtonRoute() {
    return this.showExpiredSubscriptionNavBar ? 'apps.app.expired-subscription' : 'apps.app.home';
  }

  get showExpiredSubscriptionNavBar() {
    return !!this.args.app?.canDisplayExpiredSubscriptionPage;
  }

  get showPinButton() {
    return (
      !this.showExpiredSubscriptionNavBar &&
      ((this.fullyExpanded && this.navBarExpanded) || this.navBarPinned) &&
      !this.router.currentRouteName.startsWith('apps.app.getting-started')
    );
  }

  get navBarPinned() {
    return (
      this.commonPrimaryNavBarService.pinned ||
      this.router.currentRouteName.startsWith('apps.app.getting-started')
    );
  }

  get navBarExpanded() {
    return this.expandNavbar || this.navBarPinned || this.userMenuOpen;
  }

  get inboxCount() {
    return this.args.inboxCount > 99 ? '99+' : this.args.inboxCount;
  }

  @action
  currentActiveRoute(routes: string[]) {
    return routes.some((route) => this.router.currentRouteName.startsWith(route));
  }

  @action
  handleMouseHover(expand: boolean) {
    this.timeout && clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.expandNavbar = expand;
      this.setWidgetVisibility(expand);
      this.timeout && clearTimeout(this.timeout);
    }, 200);
  }

  @action
  onMouseEnterNavItem(productId: string) {
    this.navItemInFocus = productId;
  }

  @action
  onMouseLeaveNavItem() {
    this.navItemInFocus = null;
  }

  @action
  handlePinNavBar() {
    this.expandNavbar = true;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: this.commonPrimaryNavBarService.pinned ? 'unpinned' : 'pinned',
      place: 'new-nav-bar',
    });
    this.commonPrimaryNavBarService.toggleNavBarPinStatus();
  }

  @action
  onPopoverShow() {
    this.userMenuOpen = true;
  }

  @action
  onPopoverHide() {
    this.userMenuOpen = false;
  }

  @action
  setWidgetVisibility(expand: boolean) {
    let widgetVisibility = !expand;
    if (this.commonPrimaryNavBarService?.pinned) {
      widgetVisibility = true;
    } else if (!this.commonPrimaryNavBarService?.pinned && expand) {
      widgetVisibility = false;
    }
    this.greatGuidanceService?.setWidgetVisibility(COMMON_PRIMARY_NAV, widgetVisibility);
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    CommonPrimaryNavBar: typeof CommonPrimaryNavBar;
    'common-primary-nav-bar': typeof CommonPrimaryNavBar;
  }
}
